/**
 * WHAT GETS WRITTEN BESIDE A REACTION, AND IN WHICH UNITS.
 *
 * ── WHY THIS IS NOT JUST A TEXT BOX ───────────────────────────────────────────────────────────
 * It was one, holding raw LaTeX, and a teacher who wanted ΔH had to know to type `\Delta H`.
 * There is no delta on the keyboard and the palette below writes into the reaction, not here.
 *
 * The unit is the better reason. ΔS is in JOULES where everything around it is kilojoules, and
 * getting that wrong is the classic error in a ΔG = ΔH − TΔS question — the kind a teacher can
 * publish without noticing and a class then copies. Choosing the quantity chooses the unit.
 *
 * ── CONDITIONS ARE NOT QUANTITIES, AND GO SOMEWHERE ELSE ──────────────────────────────────────
 * Temperature, pressure, catalyst and solvent go ON THE ARROW by convention — `->[900 °C]`, or
 * 450 °C above and 200 atm below with the two-slot arrow — and both already work. The one place
 * a condition reaches the quantity is standard state, which is written on the symbol itself as
 * ΔH°, so that is a tick here rather than a field of its own.
 *
 * ── THE STORED FORM DOES NOT CHANGE ───────────────────────────────────────────────────────────
 * Still the single LaTeX string `data-reaction-note` has always held, so the renderer, the
 * display path and the speech module are untouched. This builds that string and reads it back.
 * Anything it cannot read stays editable as text, the same escape the reaction itself has.
 */

/**
 * One unit a quantity can carry. An empty `latex` means the quantity is dimensionless.
 *
 * `label` is written the way the equation will be: NEGATIVE INDICES, not a solidus. It was
 * `J/(K mol)` while the dropdown was a native `<select>`, which cannot hold a superscript — so
 * the menu said one thing and the reaction showed another. SI prefers indices once a unit
 * divides more than once (a repeated solidus, `J/K/mol`, is not allowed at all), and it is what
 * the renderer has always emitted, so the label follows the output rather than the other way
 * round.
 *
 * `speak` is a locale key, because a screen reader given `\mathrm{kJ\,mol^{-1}}` says
 * "k, J, m, o, l, start superscript, minus, 1, end superscript". The unit is a known value from
 * this table, so it can simply be said.
 */
type Unit = { id: string; latex: string; label: string; speak: string };

export type Quantity = {
  id: string;
  /** The symbol in LaTeX, without the standard-state ring. */
  symbol: string;
  /** What the dropdown shows. */
  label: string;
  /** Locale key for how it is SAID — `E_\mathrm{cell}` reads as "E, c, e, l, l" otherwise. */
  speak: string;
  /** Whether a ring means anything here, and whether it is usual. */
  ring: false | 'off' | 'on';
  /** First is the default. One entry means the teacher is never asked. */
  units: Unit[];
};

const KJ_PER_MOL: Unit = { id: 'kJmol', latex: '\\mathrm{kJ\\,mol^{-1}}', label: 'kJ mol⁻¹', speak: 'reactions.units.kJmol' };
const KJ: Unit = { id: 'kJ', latex: '\\mathrm{kJ}', label: 'kJ', speak: 'reactions.units.kJ' };
const NONE: Unit = { id: 'none', latex: '', label: '—', speak: '' };

export const QUANTITIES: Quantity[] = [
  { id: 'dH', symbol: '\\Delta H', label: 'ΔH', speak: 'reactions.quantities.dH', ring: 'off', units: [KJ_PER_MOL, KJ] },
  {
    id: 'dS', symbol: '\\Delta S', label: 'ΔS', speak: 'reactions.quantities.dS', ring: 'off',
    /* Joules. The whole reason the unit is chosen for you rather than typed. */
    units: [
      { id: 'JKmol', latex: '\\mathrm{J\\,K^{-1}\\,mol^{-1}}', label: 'J K⁻¹ mol⁻¹', speak: 'reactions.units.JKmol' },
      { id: 'kJKmol', latex: '\\mathrm{kJ\\,K^{-1}\\,mol^{-1}}', label: 'kJ K⁻¹ mol⁻¹', speak: 'reactions.units.kJKmol' },
    ],
  },
  { id: 'dG', symbol: '\\Delta G', label: 'ΔG', speak: 'reactions.quantities.dG', ring: 'off', units: [KJ_PER_MOL, KJ] },
  {
    id: 'Ecell', symbol: 'E_\\mathrm{cell}', label: 'E cell', speak: 'reactions.quantities.Ecell', ring: 'on',
    /* Ringed by default: a cell potential quoted without one is unusual enough to be a mistake. */
    units: [{ id: 'V', latex: '\\mathrm{V}', label: 'V', speak: 'reactions.units.V' }, { id: 'mV', latex: '\\mathrm{mV}', label: 'mV', speak: 'reactions.units.mV' }],
  },
  { id: 'Ea', symbol: 'E_\\mathrm{a}', label: 'Ea', speak: 'reactions.quantities.Ea', ring: false, units: [KJ_PER_MOL, { id: 'Jmol', latex: '\\mathrm{J\\,mol^{-1}}', label: 'J mol⁻¹', speak: 'reactions.units.Jmol' }] },
  /* The equilibrium constants that sit beside an equation rather than in prose. Dimensionless as
     offered: exam boards disagree about K's units and a teacher who needs them has the text
     escape, which is a better answer than guessing on their behalf. */
  { id: 'K', symbol: 'K', label: 'K', speak: 'reactions.quantities.K', ring: false, units: [NONE] },
  { id: 'Kc', symbol: 'K_\\mathrm{c}', label: 'Kc', speak: 'reactions.quantities.Kc', ring: false, units: [NONE] },
  { id: 'Kp', symbol: 'K_\\mathrm{p}', label: 'Kp', speak: 'reactions.quantities.Kp', ring: false, units: [NONE] },
  { id: 'Ksp', symbol: 'K_\\mathrm{sp}', label: 'Ksp', speak: 'reactions.quantities.Ksp', ring: false, units: [NONE] },
  {
    id: 'tHalf', symbol: 't_{1/2}', label: 't½', speak: 'reactions.quantities.tHalf', ring: false,
    units: [
      { id: 's', latex: '\\mathrm{s}', label: 's', speak: 'reactions.units.s' },
      { id: 'min', latex: '\\mathrm{min}', label: 'min', speak: 'reactions.units.min' },
      { id: 'h', latex: '\\mathrm{h}', label: 'h', speak: 'reactions.units.h' },
      { id: 'd', latex: '\\mathrm{d}', label: 'd', speak: 'reactions.units.d' },
      { id: 'y', latex: '\\mathrm{y}', label: 'y', speak: 'reactions.units.y' },
    ],
  },
];

export type Entry = { quantity: string; ring: boolean; value: string; unit: string };

export const quantityById = (id: string): Quantity | undefined => QUANTITIES.find(q => q.id === id);

/** A fresh row, with the ring and unit this quantity usually wants. */
export function newEntry(id = 'dH'): Entry {
  const q = quantityById(id) ?? QUANTITIES[0]!;
  return { quantity: q.id, ring: q.ring === 'on', value: '', unit: q.units[0]!.id };
}

/** Switching the quantity keeps the number and takes the new quantity's unit and ring. */
export function retarget(entry: Entry, id: string): Entry {
  const q = quantityById(id) ?? QUANTITIES[0]!;
  return { quantity: q.id, ring: q.ring === 'on', value: entry.value, unit: q.units[0]!.id };
}

// ── Entries → the one stored string ──────────────────────────────────────────

const SEPARATOR = ',\\ ';

function entrySource(entry: Entry): string {
  const q = quantityById(entry.quantity);
  if (!q) return '';
  const unit = q.units.find(u => u.id === entry.unit) ?? q.units[0]!;
  const ring = entry.ring && q.ring !== false ? '^\\circ' : '';
  const tail = entry.value && unit.latex ? `\\ ${unit.latex}` : '';
  return `${q.symbol}${ring} = ${entry.value}${tail}`;
}

export const noteFromEntries = (entries: Entry[]): string =>
  entries.map(entrySource).filter(Boolean).join(SEPARATOR);

// ── The stored string → entries, or nothing ──────────────────────────────────

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Read a note back, or say it cannot.
 *
 * ALL of it has to parse or none of it does: showing half a note in rows and silently dropping
 * the rest would lose a teacher's work, and the text escape costs nothing. Older notes spell the
 * thin space as `\,` or as `\ ` depending on who typed them, so both are accepted.
 */
export function entriesFromNote(note: string): Entry[] | null {
  const trimmed = note.trim();
  if (!trimmed) return [];

  const parts = trimmed.split(SEPARATOR);
  const entries: Entry[] = [];
  for (const part of parts) {
    const entry = parseOne(part.trim());
    if (!entry) return null;
    entries.push(entry);
  }
  return entries;
}

function parseOne(part: string): Entry | null {
  for (const q of [...QUANTITIES].sort((a, b) => b.symbol.length - a.symbol.length)) {
    for (const ring of [true, false]) {
      const head = `${escape(q.symbol)}${ring ? escape('^\\circ') : ''}\\s*=\\s*`;
      const m = new RegExp(`^${head}(.*)$`).exec(part);
      if (!m) continue;
      const rest = m[1]!.trim();

      for (const unit of q.units) {
        if (!unit.latex) continue;
        // `\,` and `\ ` are the same thin space to a reader and to KaTeX.
        const loose = escape(unit.latex).replace(/\\\\,/g, '(?:\\\\,|\\\\ )');
        const withUnit = new RegExp(`^(.*?)\\s*(?:\\\\ |\\s)${loose}$`).exec(rest);
        if (withUnit) return { quantity: q.id, ring, value: withUnit[1]!.trim(), unit: unit.id };
      }
      // No unit found: only valid where the quantity has none, or the value is empty.
      const dimensionless = q.units.every(u => !u.latex);
      if ((dimensionless || !rest) && !/\\mathrm/.test(rest)) {
        return { quantity: q.id, ring, value: rest, unit: q.units[0]!.id };
      }
      return null;
    }
  }
  return null;
}

// ── Saying it out loud ───────────────────────────────────────────────────────

/**
 * The note as a sentence, or null when it is not one of ours.
 *
 * The maths announcer reads `\mathrm{kJ\,mol^{-1}}` the only way it can — "k, J, m, o, l, start
 * superscript, minus, 1, end superscript" — because to it they are letters. Here they are a
 * chosen unit from a table above, so they can simply be said. The VALUE still goes through the
 * announcer, because `1.8 \times 10^{-10}` is maths and should be read as maths.
 *
 * Null means the note was typed by hand, and the caller falls back to announcing the LaTeX.
 */
export function noteSpeech(
  note: string,
  t: (key: string) => string,
  sayValue: (latex: string) => string,
): string | null {
  const entries = entriesFromNote(note);
  if (!entries || !entries.length) return null;

  const said = entries.map(e => {
    const q = quantityById(e.quantity);
    if (!q) return null;
    const unit = q.units.find(u => u.id === e.unit) ?? q.units[0]!;
    const name = e.ring && q.ring !== false
      ? `${t('reactions.speech.standard')} ${t(q.speak)}`
      : t(q.speak);
    const parts = [name, t('reactions.speech.equals')];
    if (e.value) parts.push(sayValue(e.value));
    if (e.value && unit.speak) parts.push(t(unit.speak));
    return parts.join(', ');
  });

  return said.some(x => x === null) ? null : said.join(', ');
}
